/**
 * Ported from the-answer-2 (BACKLOG §16: lift the components, not the app).
 * Behaviour is theirs; the edges are savvy's. Changes made on the way in:
 *   - the field's identity is a stable string `id`, not a client-assigned
 *     `callTypeFormFieldId` number sitting beside an unrelated `id`;
 *   - a section is a flat `group` name, not a nested object with its own id;
 *   - drafts, feature announcements, router state and toasts are dropped —
 *     they belong to the host app, not the component.
 */


import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  CheckCircle,
} from "lucide-react";
import type { FormField } from "./types";
import { cn } from "@/lib/utils";
import { deriveFieldKey } from "./types";
import { FIELD_PRESETS } from "./form-elements";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { SortableElementOption } from "./sortable-field-option";

const STANDARD_KEYS = [
  "first_name",
  "last_name",
  "on_behalf_of",
  "phone_number",
  // "email", // email is standard field but we want to allow multiple eamil fields per call type
  "message_text",
];

interface ElementEditorProps {
  element: FormField;
  onUpdate: (element: FormField) => void;
  onTypeChange?: (type: FormField["type"]) => void;
}

export function FormFieldEditor({
  element,
  onUpdate,
  onTypeChange,
}: ElementEditorProps) {
  const [editedElement, setEditedElement] = useState<FormField>({
    ...element,
  });
  const [newOptionLabel, setNewOptionLabel] = useState("");

  // Update state when selected element changes
  useEffect(() => {
    setEditedElement({ ...element });
  }, [element.id, element]);

  function handleChange<K extends keyof FormField>(field: K, value: FormField[K]) {
    const updatedElement: FormField = { ...editedElement, [field]: value };

    // Renaming a label may carry the key along — but only while the key still
    // LOOKS derived from the old label. Once the operator has edited the key
    // by hand it is a deliberate contract with delivery templates, and a
    // later label tweak must not silently break it.
    //
    // (Upstream re-derived the key on every label edit unless it was one of
    // the standard keys. That quietly renames the field delivery reads by.)
    if (field === "label" && typeof value === "string") {
      const wasDerived = editedElement.key === deriveFieldKey(editedElement.label);
      if (wasDerived && !STANDARD_KEYS.includes(editedElement.key)) {
        updatedElement.key = deriveFieldKey(value);
      }
    }

    setEditedElement(updatedElement);
    onUpdate(updatedElement);
  }

  const addOption = () => {
    if (!newOptionLabel.trim()) return;

    const newOption = newOptionLabel;
    const updatedElement = {
      ...editedElement,
      options: [...(editedElement.options || []), newOption],
    };

    setEditedElement(updatedElement);
    onUpdate(updatedElement);
    setNewOptionLabel("");
  };

  const removeOption = (index: number) => {
    const updatedElement = {
      ...editedElement,
      options: editedElement.options?.filter((_, i) => i !== index),
    };
    setEditedElement(updatedElement);
    onUpdate(updatedElement);
  };

  const updateOption = (index: number, newOption: string) => {
    const updatedOptions = [...(editedElement.options || [])];
    if (updatedOptions[index] !== undefined) {
      updatedOptions[index] = newOption;
    }
    const updatedElement = {
      ...editedElement,
      options: updatedOptions,
    };
    setEditedElement(updatedElement);
    onUpdate(updatedElement);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end event
  // const handleDragEnd = (event: DragEndEvent) => {
  //   const { active, over } = event;
  //   if (!over || active.id === over.id) return;

  //   const activeIndex = editedElement.options?.findIndex(
  //     (option, index) => `${element.id}-option-${index}` === active.id
  //   );

  //   const overIndex = editedElement.options?.findIndex(
  //     (option, index) => `${element.id}-option-${index}` === over.id
  //   );

  //   if (activeIndex === undefined || overIndex === undefined) return;

  //   const updatedOptions = [...(editedElement.options || [])];
  //   const [movedOption] = updatedOptions.splice(activeIndex, 1);

  //   updatedOptions.splice(overIndex, 0, movedOption);
  //   const updatedElement = {
  //     ...editedElement,
  //     options: updatedOptions,
  //   };
  //   setEditedElement(updatedElement);
  //   onUpdate(updatedElement);
  // };
  // Handle drag end event
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const getOptionIndex = (id: string | number) => {
      if (!editedElement.options) return -1;
      return editedElement.options.findIndex(
        (option, index) => `${index}-${option}-${editedElement.id}` === id
      );
    };

    const oldIndex = getOptionIndex(active.id);
    const newIndex = getOptionIndex(over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const updatedOptions = [...(editedElement.options || [])];
    const [movedOption] = updatedOptions.splice(oldIndex, 1);
    if (movedOption === undefined) return;
    updatedOptions.splice(newIndex, 0, movedOption);
    const updatedElement = {
      ...editedElement,
      options: updatedOptions,
    };
    setEditedElement(updatedElement);
    onUpdate(updatedElement);
  };

  // }, [editedElement.url, onValidityChange]);

  const isStandardField = STANDARD_KEYS.includes(editedElement.key);

  return (
    <div className="space-y-5">
      <section id="general" className="w-full">
        <div className="border-b">
          <header className="text-lg font-medium py-2">General Settings</header>
          <div className="pt-2 pb-3">
            <div className="space-y-4">
              {
                // hide type selector if the element type is not in presets

                !FIELD_PRESETS.find(
                  (preset) => preset.type === element.type
                ) ? null : (
                  <div
                    className={cn("flex items-center gap-4", {
                      hidden:
                        element.type === "note" ||
                        !element.editable ||
                        isStandardField,
                    })}
                  >
                    <Label
                      htmlFor={`element-type-${element.id}`}
                      className="text-xs font-medium"
                    >
                      Type
                    </Label>
                    <Select
                      value={editedElement.type}
                      disabled={!element.editable}
                      onValueChange={(value) =>
                        onTypeChange?.(value as FormField["type"])
                      }
                    >
                      <SelectTrigger
                        id={`element-type-${element.id}`}
                        className="w-[200px]"
                      >
                        <SelectValue placeholder="Select field type" />
                      </SelectTrigger>
                      <SelectContent>
                        {FIELD_PRESETS.map((preset) => (
                          <SelectItem key={preset.type} value={preset.type}>
                            {preset.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )
              }

              <div
                className={cn("space-y-2", {
                  hidden: element.type === "note" || !element.editable,
                })}
              >
                <Label
                  htmlFor={`element-label-${element.id}`}
                  className="text-xs font-medium"
                >
                  Label
                </Label>
                <Input
                  id={`element-label-${element.id}`}
                  value={editedElement.label}
                  onChange={(e) => handleChange("label", e.target.value)}
                  readOnly={element.type === "note" || !element.editable}
                  disabled={element.type === "note" || !element.editable}
                />
              </div>

              {/* The key. Upstream never showed it, which made the semantic
                  name — the thing delivery templates read — invisible and
                  unsettable. It follows the label until it is edited here,
                  and then it stops following. */}
              {element.type !== "note" && (
                <div className="space-y-2">
                  <Label
                    htmlFor={`element-key-${element.id}`}
                    className="text-xs font-medium"
                  >
                    Key
                  </Label>
                  <Input
                    id={`element-key-${element.id}`}
                    value={editedElement.key}
                    onChange={(e) => handleChange("key", e.target.value)}
                    disabled={!element.editable || isStandardField}
                    aria-invalid={!/^[a-z][a-z0-9_]*$/.test(editedElement.key)}
                    className="font-mono text-sm"
                  />
                  <p className="text-muted-foreground text-xs">
                    {isStandardField
                      ? "A standard key. Delivery relies on it, so it cannot be changed."
                      : "How delivery templates and integrations name this value. Lowercase letters, digits and underscores."}
                  </p>
                </div>
              )}

              {(element.type === "text" ||
                element.type === "textarea" ||
                element.type === "phone" ||
                element.type === "email") && (
                <div
                  className={cn("space-y-2", {
                    hidden: !element.editable || isStandardField,
                  })}
                >
                  <Label
                    htmlFor={`element-placeholder-${element.id}`}
                    className="text-xs font-medium"
                  >
                    Placeholder
                  </Label>
                  <Input
                    id={`element-placeholder-${element.id}`}
                    value={editedElement.placeholder || ""}
                    onChange={(e) =>
                      handleChange("placeholder", e.target.value)
                    }
                    disabled={!element.editable}
                  />
                </div>
              )}

              {!["note", "link"].includes(element.type) && (
                <div
                  className={cn("space-y-2", {
                    hidden: !element.editable,
                  })}
                >
                  <Label
                    htmlFor={`element-description-${element.id}`}
                    className="text-xs font-medium"
                  >
                    Description
                  </Label>
                  <Textarea
                    id={`element-description-${element.id}`}
                    value={editedElement.description || ""}
                    onChange={(e) =>
                      handleChange("description", e.target.value)
                    }
                    placeholder="Add a description to help users understand this field"
                    className="min-h-[80px] text-sm"
                    disabled={!element.editable}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label
                  htmlFor={`element-note-${element.id}`}
                  className="text-xs font-medium"
                >
                  {element.type === "note" ? "Note Content" : "Note"}
                </Label>
                <Textarea
                  id={`element-note-${element.id}`}
                  value={editedElement.note || ""}
                  onChange={(e) => handleChange("note", e.target.value)}
                  placeholder="Add a note to help users understand this field"
                  className="min-h-[80px] text-sm"
                />
              </div>
            </div>

            {element.type === "link" && (
              <div className="space-y-2 mt-4">
                <Label
                  htmlFor={`element-url-${element.id}`}
                  className="text-xs font-medium"
                >
                  Link URL
                </Label>
                <Input
                  id={`element-url-${element.id}`}
                  value={editedElement.url || ""}
                  onChange={(e) => handleChange("url", e.target.value)}
                  placeholder="https://…"
                />
              </div>
            )}
          </div>
        </div>

        {element.type === "radio-group" && (
          <div className="border-b">
            <header className="text-sm font-medium py-2">Options</header>
            <div className="pt-2 pb-3">
              <div className="space-y-3">
                <DndContext
                  sensors={sensors}
                  onDragEnd={handleDragEnd}
                  collisionDetection={closestCenter}
                >
                  <SortableContext
                    items={
                      editedElement.options?.map(
                        (option, index) =>
                          `${index}-${option}-${editedElement.id}`
                      ) || []
                    }
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {editedElement.options?.map((option, index) => (
                        <SortableElementOption
                          key={`${index}-${option}-${editedElement.id}`}
                          option={option}
                          element={editedElement}
                          index={index}
                          onRemoveOption={removeOption}
                          onUpdateOption={updateOption}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
                <div className="flex gap-2">
                  <Input
                    placeholder="Add new option"
                    value={newOptionLabel}
                    onChange={(e) => setNewOptionLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        addOption();
                      }
                    }}
                    aria-label="New option text"
                  />
                  <Button
                    size="icon-xs"
                    variant="secondary"
                    className="rounded-md"
                    onClick={addOption}
                    disabled={!newOptionLabel.trim()}
                    aria-label="Add option"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </Button>
                </div>
                {editedElement.options?.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    Add at least one option for the radio group
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {!["note", "link"].includes(element.type) &&
          element.editable &&
          !isStandardField && (
            <div>
              <header className="text-lg font-medium py-2">Validation</header>
              <div className="pt-2 pb-3">
                <div className="flex items-center justify-between py-2">
                  <Label
                    htmlFor={`element-required-${element.id}`}
                    className="text-sm cursor-pointer"
                  >
                    Required field
                  </Label>
                  <Switch
                    disabled={!element.editable}
                    id={`element-required-${element.id}`}
                    checked={editedElement.required || false}
                    onCheckedChange={(checked) =>
                      handleChange("required", checked)
                    }
                  />
                </div>
              </div>
            </div>
          )}
      </section>
    </div>
  );
}
